import memoizeOne from 'memoize-one';

import type { Block, Inline } from '@gitbook/slate';
import { assert, filterOutNullable } from '@gitbook/util-ts';

interface IPositionable {
    start: number;
    end: number;
}

interface IToken {
    content: string;
}

export type PositionedToken = IPositionable & IToken;
export type PositionedInline = { inline: Inline; start: number; end: number };
export type HighlightedToken =
    | { type: 'shiki'; token: PositionedToken }
    | { type: 'annotation' | 'expression'; body: string; children: HighlightedToken[] };

export const indexPositionedTokensAndInlines = memoizeOne(
    (props: {
        tokens: Array<{ content: string }[]>;
        inlines: Array<PositionedInline>;
        node: Block;
    }) => {
        const { tokens, inlines, node } = props;
        let currentTokenIndex = 0;
        // parse tokens and inlines
        const tokenLines = tokens.map((line, index) => {
            const codeBlockLine = node.blockNodes.get(index);
            assert(codeBlockLine, 'Expect there to be a code block line for each token line');

            const tokenResults: Array<HighlightedToken> = [];

            const eatToken = () => {
                const token = line.shift();
                if (token) {
                    currentTokenIndex += token.content.length;
                    return {
                        ...token,
                        start: currentTokenIndex - token.content.length,
                        end: currentTokenIndex,
                    };
                }
                return null;
            };

            while (line.length > 0) {
                tokenResults.push(...match(eatToken, inlines).filter(filterOutNullable));
            }

            currentTokenIndex += 1; // for a newline

            return {
                tokens: tokenResults,
                node: codeBlockLine,
            };
        });

        return tokenLines;
    }
);

function match(eat: () => PositionedToken | null, inlines: Array<PositionedInline>) {
    const token = eat();
    if (!token) {
        return [];
    }

    const filteredInlines = inlines.filter(
        (inline) => inline.start >= token.start && inline.start < token.end
    );

    const result: Array<HighlightedToken> = [];
    let currentToken = token;
    const matchInline = () => {
        const inline = filteredInlines.shift();
        if (!inline) {
            result.push({
                type: 'shiki',
                token: currentToken,
            });

            return;
        }

        const [before, afterBefore] = splitTokenAt(currentToken, inline.start);

        if (before) {
            result.push({
                type: 'shiki',
                token: before,
            });
        }
        if (!afterBefore) {
            throw new Error('expect afterBefore to not be empty');
        }

        currentToken = afterBefore;
        const children: Array<{ type: 'shiki'; token: PositionedToken }> = [];

        // If shiki token finished before the end of the annotation or the annotation contains multiple tokens
        while (currentToken.end < inline.end) {
            children.push({
                type: 'shiki',
                token: currentToken,
            });

            const next = eat();
            if (!next) {
                throw new Error('expect token to not be empty');
            }
            currentToken = next;
        }

        const [inside, after] = splitTokenAt(currentToken, inline.end);
        if (!inside) {
            throw new Error('expect inside to not be empty');
        }

        children.push({
            type: 'shiki',
            token: inside,
        });

        result.push({
            type: inline.inline.type as 'annotation' | 'expression',
            body:
                inline.inline.type === 'annotation'
                    ? inline.inline.text
                    : children.map((c) => c.token.content).join(''),
            children,
        });

        if (after) {
            currentToken = after;
            matchInline();
        }
    };

    matchInline();
    return result;
}

function splitTokenAt(token: PositionedToken, absoluteIndex: number) {
    assert(
        absoluteIndex >= token.start && absoluteIndex <= token.end,
        `Index ${absoluteIndex} is out of bounds (${token.start}:${token.end})`
    );
    const before = sliceToken(token, 0, absoluteIndex - token.start);
    const after = sliceToken(token, absoluteIndex - token.start, token.end - absoluteIndex);
    return [
        isEmptyPositionedToken(before) ? null : before,
        isEmptyPositionedToken(after) ? null : after,
    ];
}

function sliceToken(token: PositionedToken, relativeStart: number, relativeLength: number) {
    const start = token.start + relativeStart;
    const end = start + relativeLength;
    return {
        ...token,
        start,
        end,
        content: token.content.slice(relativeStart, relativeStart + relativeLength),
    };
}

function isEmptyPositionedToken(token: IPositionable): boolean {
    return token.start === token.end;
}
